import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Heart, MapPin, Award } from "lucide-react";
import { useSEO } from "../hooks/useSEO";

const IMG_FARMER = "/images/Exploitation-champ.jpg";
const IMG_LANDSCAPE = "/images/Champ-8.jpeg";
const IMG_CHAMP = "/images/Arrosage-1.webp";

const valeurs = [
  { icon: Leaf, title: "Agriculture durable", desc: "Zéro pesticide, zéro engrais chimique. Nous cultivons le miscanthus dans le respect total de l'environnement et de la biodiversité locale." },
  { icon: Heart, title: "Passion & transmission", desc: "Notre amour du terroir sarthois et notre passion pour l'agriculture durable guident chacune de nos décisions. Nous cultivons pour les générations futures." },
  { icon: MapPin, title: "Ancrage local", desc: "Nous sommes fiers d'être une exploitation 100% locale. Nos produits sont cultivés, récoltés et conditionnés en Sarthe, réduisant l'empreinte carbone de chaque livraison." },
  { icon: Award, title: "Qualité irréprochable", desc: "Chaque big bag de rhizomes, chaque livraison de paille est contrôlé pour garantir la satisfaction de nos clients et la réussite de leurs projets." },
];

export default function LExploitation() {
  useSEO({
    title: "Notre exploitation à Torcé-en-Vallée (Sarthe) | Miscanthus Sarthe",
    description: "EARL Torcé Biomasse Energie : exploitation familiale sarthoise spécialisée dans le Miscanthus giganteus. Zéro pesticide, production 100 % locale, agriculture durable.",
    path: "/exploitation",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="exploitation-page" style={{ backgroundColor: "#f5f2ec" }}>
      {/* Header */}
      <div className="page-header" style={{ paddingTop: "7rem" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-divider mb-4" />
          <h1 className="font-display text-5xl sm:text-6xl mb-5" style={{ color: "#3a2b24" }}>L'Exploitation</h1>
          <p className="text-base max-w-2xl" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>
            EARL Torcé Biomasse Energie — Une exploitation familiale en Sarthe, engagée dans l'agriculture durable depuis plusieurs années.
          </p>
        </div>
      </div>

      {/* Hero image */}
      <div className="relative h-72 sm:h-96 overflow-hidden">
        <img src={IMG_LANDSCAPE} alt="Paysage sarthois" className="w-full h-full object-cover" style={{ objectPosition: "center 20%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(245,242,236,1) 0%, transparent 60%)" }} />
      </div>

      {/* Histoire */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl mb-6" style={{ color: "#3a2b24" }}>Notre histoire</h2>
              <div className="space-y-4 text-base leading-relaxed" style={{ color: "#3a2b24", opacity: 0.8, fontFamily: "'Satoshi', sans-serif" }}>
                <p>
                  L'EARL Torcé Biomasse Energie est née d'une conviction : il est possible de cultiver de manière intensive tout en respectant l'environnement et les ressources naturelles. C'est cette vision qui nous a conduits vers le miscanthus.
                </p>
                <p>
                  Implantée à Torcé en Vallée, au cœur de la Sarthe, notre exploitation familiale a développé une expertise unique dans la culture du <em>Miscanthus giganteus</em>. Après plusieurs années de recherches et d'expérimentations, nous maîtrisons aujourd'hui toutes les étapes de la production, de la sélection des rhizomes à la commercialisation des produits finis.
                </p>
                <p>
                  Notre territoire — la Sarthe et les Pays de la Loire — bénéficie d'un climat tempéré idéal pour la culture du miscanthus. Nous sommes fiers de contribuer au développement de cette filière en pleine croissance dans notre région.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <img src={IMG_FARMER} alt="L'exploitation EARL Torcé" loading="lazy" decoding="async" className="rounded-2xl w-full object-cover shadow-lg" style={{ aspectRatio: "3/4" }} />
            </div>
          </div>
        </div>
      </section>

      {/* Chiffres clés */}
      <section style={{ backgroundColor: "#eae4d8" }} className="py-16 px-4 sm:px-6 lg:px-8" data-testid="chiffres-section">
        <div className="max-w-7xl mx-auto">
          <div className="section-divider mb-4" />
          <h2 className="font-display text-3xl mb-12" style={{ color: "#3a2b24" }}>L'exploitation en chiffres</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { num: "15+", label: "Ans de durée de vie d'une plantation" },
              { num: "0", label: "Pesticides ou herbicides utilisés" },
              { num: "10-25t", label: "MS/ha à pleine maturité" },
              { num: "100%", label: "Local — Sarthe, Pays de la Loire" },
            ].map((c, i) => (
              <div key={i} className="text-center p-6 rounded-xl" style={{ backgroundColor: "white", border: "1px solid #d8d1c4" }} data-testid={`chiffre-${i}`}>
                <span className="font-display text-4xl sm:text-5xl" style={{ color: "#2d5016" }}>{c.num}</span>
                <p className="text-sm mt-2" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>{c.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="valeurs-section">
        <div className="max-w-7xl mx-auto">
          <div className="section-divider mb-4" />
          <h2 className="font-display text-3xl mb-12" style={{ color: "#3a2b24" }}>Nos Valeurs</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {valeurs.map((v, i) => (
              <div key={i} className="p-6 rounded-xl flex gap-4" style={{ backgroundColor: "white", border: "1px solid #d8d1c4" }}>
                <v.icon size={28} style={{ color: "#2d5016", flexShrink: 0 }} />
                <div>
                  <h3 className="font-display text-xl mb-2" style={{ color: "#3a2b24" }}>{v.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pratiques agricoles */}
      <section style={{ backgroundColor: "#eae4d8" }} className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src={IMG_CHAMP} alt="Champ de miscanthus" loading="lazy" decoding="async" className="rounded-2xl w-full object-cover" style={{ aspectRatio: "16/9" }} />
            </div>
            <div>
              <h2 className="font-display text-3xl mb-6" style={{ color: "#3a2b24" }}>Nos pratiques agricoles</h2>
              <div className="space-y-4 text-sm leading-relaxed" style={{ color: "#3a2b24", opacity: 0.8, fontFamily: "'Satoshi', sans-serif" }}>
                <p>
                  La culture du miscanthus s'inscrit naturellement dans une démarche d'agriculture durable. <strong>Aucun pesticide, aucun herbicide, aucun engrais chimique</strong> n'est nécessaire après l'établissement de la plantation.
                </p>
                <p>
                  Grâce à son couvert végétal dense, le miscanthus protège naturellement le sol contre l'érosion et favorise la vie microbienne. Ses racines profondes améliorent la structure du sol et participent à la séquestration du carbone.
                </p>
                <p>
                  Nous travaillons en étroite collaboration avec les organismes agricoles locaux pour continuer à améliorer nos pratiques et partager notre expérience avec d'autres agriculteurs de la région.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: "#2d5016" }} className="py-12 px-4 text-center">
        <h2 className="font-display text-3xl mb-4" style={{ color: "#f5f2ec" }}>Vous souhaitez nous rendre visite ?</h2>
        <p className="text-sm mb-6 opacity-75" style={{ color: "#eae4d8", fontFamily: "'Satoshi', sans-serif" }}>
          Nous sommes disponibles pour vous accueillir sur l'exploitation et répondre à toutes vos questions.
        </p>
        <Link to="/contact" className="btn-secondary inline-flex items-center gap-2 px-8 py-3.5 rounded-md font-body font-500 text-base" data-testid="exploitation-contact-cta">
          Nous contacter <ArrowRight size={18} />
        </Link>
      </section>
    </main>
  );
}
